import { ThemeEvents } from '@theme/events';

/**
 * Category navigation without a full page load.
 *
 * Clicking a sidebar category or a hero category tab fetches the target
 * collection page in the background, swaps the products section (grid,
 * filters and sidebar arrive server-rendered for the new collection) in
 * place, and pushes the new URL. Back/forward retrace the swaps via
 * popstate — which also repairs the stock facets behavior, where Back
 * after a filter change updated the URL but not the products.
 */

const CATEGORY_LINK_SELECTOR = 'a.collection-sidebar-nav__link, a.collection-category-tab';

/** @type {AbortController | null} */
let swapController = null;

/** The pathname + search this document is currently displaying. */
let renderedUrl = window.location.pathname + window.location.search;

/**
 * The section holding whatever the current collection page shows in place of
 * products. Usually a `<results-list>` grid, but Shop All renders category
 * carousels instead and has no results list — hence the marker attribute,
 * which both branches of sections/main-collection carry.
 */
function productsSection() {
  return document.querySelector('[data-collection-products]')?.closest('.shopify-section') ?? null;
}

/**
 * Inline <script> tags inside HTML inserted via DOM APIs never execute.
 * The swapped section relies on a few (sidebar accordion, stored grid
 * view), so clone each into a fresh node, which does execute.
 * @param {Element} root
 */
function runInlineScripts(root) {
  for (const original of root.querySelectorAll('script')) {
    const type = original.getAttribute('type');
    if (original.src || (type && type !== 'text/javascript')) continue;

    const script = document.createElement('script');
    script.textContent = original.textContent;
    original.replaceWith(script);
  }
}

/**
 * Swaps a whole collection-dependent section that carries no JS state, by
 * locating its `#shopify-section-…` wrapper. The wrapper id is read from
 * whichever document actually rendered `selector`, so a section that is
 * empty on one of the two collections (hub links are per-collection
 * metafields) still swaps in both directions. When neither page renders it
 * there is nothing to swap.
 *
 * @param {Document} doc - The fetched page.
 * @param {string} selector - Content inside the section, present only when it renders.
 */
function swapContentSection(doc, selector) {
  const id =
    document.querySelector(selector)?.closest('.shopify-section')?.id ??
    doc.querySelector(selector)?.closest('.shopify-section')?.id;
  if (!id) return;

  const current = document.getElementById(id);
  const fresh = doc.getElementById(id);
  if (current && fresh) current.replaceWith(fresh);
}

/**
 * The browse hero sits above the swapped products section, so its copy —
 * H1, SEO intro, safety-notes subtitle, research link — and its hero image
 * stay on the previous collection unless they are swapped too.
 *
 * Only the copy and the image move: the search bar keeps its listeners and
 * whatever the shopper has typed, and the category tabs keep their scroll
 * position (syncCategoryTabs restyles the active one).
 *
 * @param {Document} doc - The fetched page.
 */
function syncBrowseHero(doc) {
  const current = document.querySelector('.collection-browse-hero');
  const fresh = doc.querySelector('.collection-browse-hero');
  if (!current || !fresh) return;

  // Subcategory pages drop the hero media, which changes both layouts.
  current.classList.toggle(
    'collection-browse-hero--stacked',
    fresh.classList.contains('collection-browse-hero--stacked')
  );

  const currentLayout = current.querySelector('.collection-browse-hero__layout');
  const freshLayout = fresh.querySelector('.collection-browse-hero__layout');
  if (!currentLayout || !freshLayout) return;

  currentLayout.classList.toggle(
    'collection-browse-hero__layout--no-image',
    freshLayout.classList.contains('collection-browse-hero__layout--no-image')
  );

  // Imageless heroes put the copy beside the heading, but only when there is
  // copy to put there — which changes with the collection.
  currentLayout.classList.toggle(
    'collection-browse-hero__layout--split',
    freshLayout.classList.contains('collection-browse-hero__layout--split')
  );

  const currentText = currentLayout.querySelector('.collection-browse-hero__text');
  const freshText = freshLayout.querySelector('.collection-browse-hero__text');
  if (currentText && freshText) {
    const isSearchBar = (/** @type {Element} */ node) => node.classList.contains('hsb__bar');
    const bar = [...currentText.children].find(isSearchBar);

    for (const node of [...currentText.children]) {
      if (node !== bar) node.remove();
    }

    const copy = [...freshText.children].filter((node) => !isSearchBar(node));
    if (bar) bar.before(...copy);
    else currentText.append(...copy);
  }

  const currentImage = currentLayout.querySelector('.collection-browse-hero__image-wrapper');
  const freshImage = freshLayout.querySelector('.collection-browse-hero__image-wrapper');
  if (currentImage && freshImage) currentImage.replaceWith(freshImage);
  else if (currentImage) currentImage.remove();
  else if (freshImage) currentLayout.append(freshImage);
}

/**
 * The breadcrumb trail is rendered once from layout/theme.liquid rather than
 * from a section, so it sits outside everything the swap replaces and would
 * otherwise keep naming the collection the shopper just left. Both the visible
 * nav and its BreadcrumbList JSON-LD move, so crawlers reading the rendered DOM
 * see the trail that matches the URL.
 *
 * The snippet renders nothing on templates with no meaningful trail, so handle
 * either side being absent instead of assuming both pages have one.
 *
 * @param {Document} doc - The fetched page.
 */
function syncBreadcrumbs(doc) {
  const current = document.querySelector('.breadcrumbs-wrap');
  const fresh = doc.querySelector('.breadcrumbs-wrap');

  const currentSchema = document.querySelector('script[data-breadcrumb-schema]');
  const freshSchema = doc.querySelector('script[data-breadcrumb-schema]');

  if (current && fresh) current.replaceWith(fresh);
  else if (current) current.remove();
  else if (fresh) document.getElementById('MainContent')?.prepend(fresh);

  if (currentSchema && freshSchema) currentSchema.replaceWith(freshSchema);
  else if (currentSchema) currentSchema.remove();
  else if (freshSchema) fresh?.after(freshSchema);
}

/**
 * The hero tabs live outside the swapped section, so restyle the active
 * tab by hand.
 * @param {string} pathname
 */
function syncCategoryTabs(pathname) {
  for (const tab of document.querySelectorAll('a.collection-category-tab')) {
    let isActive = false;
    try {
      isActive = new URL(tab.href).pathname === pathname;
    } catch {
      // Malformed href — leave the tab inactive.
    }

    tab.classList.toggle('collection-category-tab--active', isActive);
    if (isActive) {
      tab.setAttribute('aria-current', 'page');
    } else {
      tab.removeAttribute('aria-current');
    }
  }
}

/**
 * Fetches `url` and swaps the products section in place. Falls back to a
 * normal navigation on any failure.
 *
 * @param {URL} url
 * @param {boolean} push - Whether to add a history entry (false on popstate).
 */
async function swapCollection(url, push) {
  const section = productsSection();
  if (!section) {
    window.location.assign(url.toString());
    return;
  }

  swapController?.abort();
  const controller = new AbortController();
  swapController = controller;

  section.classList.add('collection-swap-loading');
  section.setAttribute('aria-busy', 'true');

  try {
    const response = await fetch(url.toString(), { signal: controller.signal });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const fresh = doc.getElementById(section.id);
    if (!fresh) throw new Error('No matching products section in the response');

    if (push) history.pushState({}, '', url.toString());
    renderedUrl = url.pathname + url.search;

    section.replaceWith(fresh);
    runInlineScripts(fresh);

    // Everything else on the page that reads from `collection`. Done before
    // the scroll decisions below, which measure the new layout.
    syncBreadcrumbs(doc);
    syncBrowseHero(doc);
    swapContentSection(doc, '.collection-hub');

    document.title = doc.title;
    syncCategoryTabs(url.pathname);

    // The sidebar's inline script sets this flag to force a post-reload
    // scroll; there is no reload on this path, so drop it.
    sessionStorage.removeItem('birch_scrollToProducts');

    // Landing priority: a #card-id hash, then a deep page's first card
    // (?page=N restored via history), then — if the shopper had scrolled
    // past the top of the products area — the products anchor.
    const pageParam = Number(url.searchParams.get('page') ?? 1);
    const hashTarget = url.hash ? document.getElementById(url.hash.slice(1)) : null;
    const pageTarget =
      pageParam > 1 ? document.querySelector(`.product-grid__item[data-page="${pageParam}"]`) : null;
    const anchor = document.getElementById('products');

    if (hashTarget) {
      hashTarget.scrollIntoView({ behavior: 'instant', block: 'start' });
    } else if (pageTarget) {
      pageTarget.scrollIntoView({ behavior: 'instant', block: 'start' });
    } else if (anchor && anchor.getBoundingClientRect().top < 0) {
      anchor.scrollIntoView({ behavior: 'instant', block: 'start' });
    }
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') return;
    window.location.assign(url.toString());
  } finally {
    // A newer swap owns the loading state (and the section) now.
    if (swapController === controller) {
      swapController = null;
      section.classList.remove('collection-swap-loading');
      section.removeAttribute('aria-busy');
    }
  }
}

document.addEventListener('click', (event) => {
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  if (!(event.target instanceof Element)) return;

  const link = event.target.closest(CATEGORY_LINK_SELECTOR);
  if (!(link instanceof HTMLAnchorElement) || !productsSection()) return;

  const url = new URL(link.href, window.location.origin);
  if (url.origin !== window.location.origin || !url.pathname.startsWith('/collections/')) return;

  event.preventDefault();

  // Carry the chosen sort over so the swapped grid (and its sort dropdown)
  // keeps the order the shopper picked. Also keeps the section's inline
  // "default to best-selling" redirect a no-op instead of a reload.
  if (!url.searchParams.has('sort_by')) {
    const currentSort = new URLSearchParams(window.location.search).get('sort_by');
    url.searchParams.set('sort_by', currentSort || 'best-selling');
  }

  if (url.pathname === window.location.pathname && url.search === window.location.search) return;

  // On mobile the categories live inside the filters drawer, which is part of
  // the section we are about to replace. Closing it first releases the body
  // scroll lock dialog.js applies while it is open — removing the dialog from
  // the DOM instead would strand `position: fixed` on <body>.
  const dialog = link.closest('dialog-component');
  if (dialog instanceof HTMLElement && 'closeDialog' in dialog && typeof dialog.closeDialog === 'function') {
    Promise.resolve(dialog.closeDialog()).then(() => swapCollection(url, true));
    return;
  }

  swapCollection(url, true);
});

window.addEventListener('popstate', () => {
  const next = window.location.pathname + window.location.search;
  if (next === renderedUrl) return; // hash-only traversal — the browser scrolls

  if (!window.location.pathname.startsWith('/collections/') || !productsSection()) {
    window.location.reload();
    return;
  }

  swapCollection(new URL(window.location.href), false);
});

// Filter changes push their own history entries (facets.js); track them so
// popstate can tell a real traversal from the entry we already display.
document.addEventListener(ThemeEvents.FilterUpdate, () => {
  renderedUrl = window.location.pathname + window.location.search;
});
